interface CalendarDateParts {
  year: number;
  month: number;
  day: number;
}

function isLeapYear(year: number) {
  return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
}

function daysInMonth(year: number, month: number) {
  if (month === 2) return isLeapYear(year) ? 29 : 28;
  return [4, 6, 9, 11].includes(month) ? 30 : 31;
}

function parseCalendarDate(value: string): CalendarDateParts {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) throw new Error("Invalid calendar date.");
  const parts = {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3])
  };
  if (
    parts.year < 1 ||
    parts.month < 1 ||
    parts.month > 12 ||
    parts.day < 1 ||
    parts.day > daysInMonth(parts.year, parts.month)
  ) {
    throw new Error("Invalid calendar date.");
  }
  return parts;
}

function formatCalendarDate({ year, month, day }: CalendarDateParts) {
  return `${String(year).padStart(4, "0")}-${String(month).padStart(
    2,
    "0"
  )}-${String(day).padStart(2, "0")}`;
}

function addCalendarDays(value: string, days: number) {
  if (!Number.isInteger(days)) throw new Error("Invalid calendar day offset.");
  let parts = parseCalendarDate(value);
  const direction = days < 0 ? -1 : 1;
  for (let count = 0; count < Math.abs(days); count += 1) {
    if (direction > 0) {
      if (parts.day < daysInMonth(parts.year, parts.month)) {
        parts = { ...parts, day: parts.day + 1 };
      } else if (parts.month < 12) {
        parts = { year: parts.year, month: parts.month + 1, day: 1 };
      } else {
        parts = { year: parts.year + 1, month: 1, day: 1 };
      }
    } else if (parts.day > 1) {
      parts = { ...parts, day: parts.day - 1 };
    } else if (parts.month > 1) {
      const month = parts.month - 1;
      parts = { year: parts.year, month, day: daysInMonth(parts.year, month) };
    } else {
      const year = parts.year - 1;
      if (year < 1) throw new Error("Calendar date is out of range.");
      parts = { year, month: 12, day: 31 };
    }
  }
  return formatCalendarDate(parts);
}

export const MAX_FLEXIBLE_DATE_RANGE_MONTHS = 6;

export function isCalendarDate(value: string) {
  try {
    parseCalendarDate(value);
    return true;
  } catch {
    return false;
  }
}

export function addCalendarMonths(value: string, months: number) {
  if (!Number.isInteger(months)) throw new Error("Invalid calendar month offset.");
  const source = parseCalendarDate(value);
  const targetIndex = source.year * 12 + source.month - 1 + months;
  const year = Math.floor(targetIndex / 12);
  const month = (targetIndex % 12) + 1;
  if (year < 1) throw new Error("Calendar date is out of range.");
  return formatCalendarDate({
    year,
    month,
    day: Math.min(source.day, daysInMonth(year, month))
  });
}

export function earliestCalendarRangeStart(endDate: string, months: number) {
  let candidate = addCalendarMonths(endDate, -months);
  while (addCalendarMonths(candidate, months) < endDate) {
    candidate = addCalendarDays(candidate, 1);
  }
  return candidate;
}

export function isCalendarRangeWithinMonths(
  startDate: string,
  endDate: string,
  months: number
) {
  parseCalendarDate(startDate);
  parseCalendarDate(endDate);
  return startDate <= endDate && endDate <= addCalendarMonths(startDate, months);
}

export function clampCalendarRangeEnd(
  startDate: string,
  endDate: string,
  months: number
) {
  if (!isCalendarDate(startDate) || !isCalendarDate(endDate)) return endDate;
  const maximumEndDate = addCalendarMonths(startDate, months);
  return endDate > maximumEndDate ? maximumEndDate : endDate;
}

export function calendarDateRange(startDate: string, endDate: string) {
  parseCalendarDate(startDate);
  parseCalendarDate(endDate);
  if (startDate > endDate) throw new Error("Invalid calendar date range.");
  const result: string[] = [];
  let current = startDate;
  while (current <= endDate) {
    result.push(current);
    current = addCalendarDays(current, 1);
  }
  return result;
}

export function localCalendarDate(value = new Date()) {
  return formatCalendarDate({
    year: value.getFullYear(),
    month: value.getMonth() + 1,
    day: value.getDate()
  });
}

export function calendarMonthRangeInputBounds(
  startDate: string,
  endDate: string,
  months = MAX_FLEXIBLE_DATE_RANGE_MONTHS
) {
  return {
    startMin: isCalendarDate(endDate)
      ? earliestCalendarRangeStart(endDate, months)
      : undefined,
    endMax: isCalendarDate(startDate)
      ? addCalendarMonths(startDate, months)
      : undefined
  };
}
